import { useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import type { SiteRow } from "../data/routes";
import {
  ASSET_TYPES,
  ASSET_TYPE_LABEL,
  clearSiteAsset,
  fetchSiteAssets,
  saveSiteAsset,
  type AssetType,
  type SiteAsset,
} from "../data/site-asset";
import { parseGeoJsonPolygon } from "../data/site-boundary";
import { useReadModel } from "../data/useReadModel";

// PRD-001Y Y3 — asset / building zone editor (Setup). Import/paste a GeoJSON Polygon with a name and
// type to add an asset zone to a site, list the site's active zones, and clear any of them. Zones
// render as the L1 layer on the CAD Live map, between the site boundary and the checkpoints. v1 is
// GeoJSON import (MapLibre draw is a deferred follow-up). Writes go through the schedule.manage-
// gated hosted RPCs; viewers see the read-only zone list.
export function SiteAssetEditor({
  client,
  accountId,
  canManage = false,
  pickerSites,
}: {
  client: SupabaseClient;
  accountId: string;
  /** schedule.manage — add/clear affordances only exist when true (fail closed). */
  canManage?: boolean;
  pickerSites: readonly SiteRow[];
}) {
  const { status, data, error, reload } = useReadModel<SiteAsset[]>(
    () => fetchSiteAssets(client, accountId),
    [client, accountId],
  );
  const assets = useMemo(() => data ?? [], [data]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");
  const [name, setName] = useState("");
  const [assetType, setAssetType] = useState<AssetType>("building");
  const [geojsonText, setGeojsonText] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  const activeSiteId = selectedSiteId.length > 0 ? selectedSiteId : (pickerSites[0]?.siteId ?? "");
  const assetsForSite = assets.filter((asset) => asset.siteId === activeSiteId);

  function resetFeedback(): void {
    setFailure(null);
    setReceipt(null);
  }

  async function importAndSave(): Promise<void> {
    if (busy || !canManage) return;
    resetFeedback();
    if (activeSiteId.length === 0) {
      setFailure("Pick a site first.");
      return;
    }
    if (name.trim().length === 0) {
      setFailure("Name the asset zone (e.g. Building A).");
      return;
    }
    const parsed = parseGeoJsonPolygon(geojsonText);
    if (!parsed.ok) {
      setFailure(parsed.error);
      return;
    }
    setBusy(true);
    const outcome = await saveSiteAsset(client, accountId, activeSiteId, parsed.ring, {
      name: name.trim(),
      assetType,
      source: "import_geojson",
    });
    setBusy(false);
    if (outcome.ok) {
      setName("");
      setGeojsonText("");
      setReceipt("Asset zone saved.");
      reload();
    } else {
      setFailure(outcome.reason);
    }
  }

  async function clear(asset: SiteAsset): Promise<void> {
    if (busy || !canManage) return;
    resetFeedback();
    setBusy(true);
    const outcome = await clearSiteAsset(client, accountId, asset.siteAssetId);
    setBusy(false);
    if (outcome.ok) {
      setReceipt(`Cleared ${asset.name}.`);
      reload();
    } else {
      setFailure(outcome.reason);
    }
  }

  if (pickerSites.length === 0) {
    return (
      <p className="surface__state" role="status">
        Add a site in step 1–2 first; asset zones attach to a site.
      </p>
    );
  }

  return (
    <div className="operator-panel site-asset">
      <label className="tpl__field site-asset__site">
        Site
        <select
          aria-label="Site for asset zones"
          value={activeSiteId}
          onChange={(event) => {
            setSelectedSiteId(event.target.value);
            resetFeedback();
          }}
        >
          {pickerSites.map((site) => (
            <option key={site.siteId} value={site.siteId}>
              {site.siteName}
            </option>
          ))}
        </select>
      </label>

      {status === "loading" && data === null ? (
        <p className="surface__state" role="status">Loading asset zones…</p>
      ) : status === "error" ? (
        <p className="surface__state surface__state--error" role="alert">
          {error ?? "Could not load site assets."}
        </p>
      ) : assetsForSite.length === 0 ? (
        <p className="site-asset__status" role="status">No asset zones for this site yet.</p>
      ) : (
        <ul className="operator-list site-asset__list" aria-label="Asset zones">
          {assetsForSite.map((asset) => (
            <li key={asset.siteAssetId} className="operator-row site-asset__row">
              <span className="operator-row__title">{asset.name}</span>
              <span className="rollout__badge">{ASSET_TYPE_LABEL[asset.assetType]}</span>
              <span className="site-asset__meta">{asset.ring.length} points</span>
              {canManage ? (
                <button
                  type="button"
                  className="builder__cancel"
                  disabled={busy}
                  onClick={() => void clear(asset)}
                >
                  Clear
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canManage ? (
        <>
          <div className="tpl__field-row">
            <label className="tpl__field">
              Zone name
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Building A"
              />
            </label>
            <label className="tpl__field">
              Type
              <select
                aria-label="Asset type"
                value={assetType}
                onChange={(event) => setAssetType(event.target.value as AssetType)}
              >
                {ASSET_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {ASSET_TYPE_LABEL[type]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="tpl__field">
            Paste a GeoJSON polygon (Polygon, Feature, or FeatureCollection)
            <textarea
              className="site-asset__geojson"
              value={geojsonText}
              onChange={(event) => setGeojsonText(event.target.value)}
              placeholder='{"type":"Polygon","coordinates":[[[-85.76,38.25],[-85.75,38.25],[-85.75,38.26],[-85.76,38.25]]]}'
              rows={4}
            />
          </label>
          <div className="policy__editor-actions">
            <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void importAndSave()}>
              {busy ? "Saving…" : "Import & save zone"}
            </button>
          </div>
          <p className="page__note">
            Many zones per site (buildings, parking, gates). Saving a zone with an existing name
            replaces it. Zones show as the labeled asset layer on the Command Center map, under
            checkpoints.
          </p>
        </>
      ) : (
        <p className="page__note">Read-only for this role.</p>
      )}

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </div>
  );
}
